/* eslint-disable camelcase */
/* eslint-disable react/no-did-update-set-state */

/*
 * MediaGallery.jsx
 * Created by Destin Frasier on 03/27/18
 */

import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import cx from 'classnames';

import * as Icons from './icons/Icons';
import Heading from './Heading';
import { FlexCol, FlexRow } from './FlexGrid';
import MediaGalleryModal from './MediaGalleryModal';

export default class MediaGallery extends React.PureComponent {
    static propTypes = {
        /** The id of the campground/permit/ticket facility/gateway/POI that the
         * api will use to fetch the images.
         * Do not use the images prop if using this prop. */
        assetId: PropTypes.string,

        /** The id of the campsite that the api will use to fetch the images
         * Do not use the images if using this prop.
        */
        campsiteId: PropTypes.string,

        /** The tour id the api will use to fetch the images
         * Do not use the images if using this prop.
        */
        tourId: PropTypes.string,

        /** Manually provide the gallery images and related information. Do not
         * use the assetId/campsiteId/tourId props if using this prop. */
        images: PropTypes.arrayOf(PropTypes.shape({
            altText: PropTypes.string,
            caption: PropTypes.string,
            url: PropTypes.string,
            credit: PropTypes.string
        })),

        /** The heading level of the 'Media Gallery' heading */
        headingLevel: PropTypes.number,

        /** The styling of the 'Media Gallery' heading */
        headingAppearance: PropTypes.string,

        /** Shows the thumbnail strip at the bottom of the open gallery */
        hasThumbnailStrip: PropTypes.bool,

        /** Additional className added to the outer container */
        className: PropTypes.string
    };

    static defaultProps = {
        assetId: undefined,
        campsiteId: undefined,
        tourId: undefined,
        images: undefined,
        headingLevel: 2,
        headingAppearance: 'h5',
        hasThumbnailStrip: true
    };

    state = {
        showGallery: false,
        activeIndex: 0,
        triggerId: '',
        mediaItems: this.props.images || []
    };

    componentDidMount() {
        const { assetId, campsiteId, tourId, images } = this.props;

        // get the content type needed for the api URL
        let contentType;
        let id;

        if (assetId) {
            contentType = 'asset';
            id = assetId;
        }
        else if (tourId) {
            contentType = 'tour';
            id = tourId
        }
        else if (campsiteId) {
            contentType = 'campsite';
            id = campsiteId;
        }

        // Get images from api
        if (contentType && id && !images) {
            axios.get(`/api/media/public/${contentType}/${id}`, {})
                .then((res) => {
                    if (res && !res.data.err) {
                        if (res.data.result) {
                            const fetchedImages = res.data.result.filter((item) => {
                                const { is_gallery, title, url } = item;
                                if (!is_gallery || !title || !url) {
                                    return false
                                }
                                return true;
                            });

                            fetchedImages.sort((a, b) => {
                                return a.position - b.position;
                            });

                            const items = fetchedImages.map((item) => {
                                return {
                                    altText: item.title || item.description,
                                    caption: item.description,
                                    url: item.url,
                                    credit: item.credits
                                };
                            });

                            this.setState({ mediaItems: items });
                        }
                    }
                })
                .catch((error) => {
                    throw (error);
                });
        }
    }

    componentDidUpdate(prevProps) {
        // If images are passed in, update the state. This allows the user to
        // provide the images in an async fashion.
        const { images } = this.props;

        if (images !== prevProps.images) {
            this.setState({
                mediaItems: images
            });
        }
    }

    openModal = (activeIndex, triggerId) => {
        this.setState({
            activeIndex,
            triggerId: `media-block-${triggerId}`,
            showGallery: true
        });
    }

    closeModal = () => {
        this.setState({
            showGallery: false
        });

        window.setTimeout(() => {
            document.getElementById(this.state.triggerId).focus();
        }, 0);
    }

    generateImages(images) {
        return images.map((image) => ({
            url: image.photo_gallery_image,
            title: image.photo_gallery_image_alt
        }));
    }

    generateMediaBlocks() {
        let { mediaItems } = this.state;
        const mediaItemsLength = mediaItems.length;

        // Generate preview images
        // If more than 6 images exist, show "View All" block as the 6th preview item
        // Otherwise, show just the 6 or fewer preview images

        if (mediaItemsLength > 6) {
            mediaItems = mediaItems.slice(0, 5);
        }

        const mediaBlocks = mediaItems.map((mediaItem, index) => (
            <FlexCol sm={6} lg={4} key={mediaItem.url}>
                <button
                    id={`media-block-${index}`}
                    className="rec-media-gallery-item-button"
                    alt="Expand Image"
                    onClick={() => this.openModal(index, index)} >
                    <span
                        className="rec-media-gallery-item-button-inner"
                        style={{ backgroundImage: `url(${mediaItem.url})` }} />
                    <span className="rec-sr-only">{`Image ${index + 1} of ${mediaItemsLength}: ${mediaItem.altText}. Open this image in the gallery`}</span>
                </button>
            </FlexCol>
        ));

        // If the gallery has more than 6 images, show the "View All" block

        if (mediaItemsLength > 6) {
            mediaBlocks.push(
                <FlexCol sm={6} lg={4} key="rec-media-gallery-more">
                    <button
                        className="rec-media-gallery-item-button rec-media-gallery-more"
                        id="media-block-all"
                        alt="Open media gallery"
                        onClick={() => this.openModal(0, 'all')}>
                        <span className="rec-media-gallery-more-inner">
                            <Icons.IconCamera />
                            <span>
                                View all {mediaItemsLength} photos
                            </span>
                        </span>
                    </button>
                </FlexCol>
            );
        }

        return mediaBlocks;
    }

    render() {
        const { headingLevel, headingAppearance, className } = this.props;
        const { mediaItems } = this.state;

        if (!mediaItems || !mediaItems.length) {
            return null;
        }

        return (
            <div className={cx('rec-media-gallery', className)} data-component="MediaGallery">
                <Heading
                    headingLevel={headingLevel}
                    appearance={headingAppearance}>
                    Media Gallery
                </Heading>

                <FlexRow>
                    {this.generateMediaBlocks()}
                </FlexRow>

                <MediaGalleryModal
                    images={mediaItems}
                    isOpen={this.state.showGallery}
                    onRequestClose={this.closeModal}
                    activeIndex={this.state.activeIndex}
                    hasThumbnailStrip={this.props.hasThumbnailStrip} />
            </div>
        );
    }
}
